import React, { useEffect, useState } from 'react';
import { Shield, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

/**
 * Keycloak SSO Callback Page
 * 
 * Bu sayfa popup pencere içinde açılır.
 * Keycloak'tan gelen authorization code'u ana pencereye iletir.
 */
const KeycloakCallbackPage = () => {
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
            setStatus('error');
            setErrorMessage(errorDescription || error || 'Giriş işlemi iptal edildi');
            // Close popup after showing error
            setTimeout(() => {
                window.close();
            }, 3000);
            return;
        }

        if (code) {
            // state parametresinden tenant_id çıkar
            let tenantId = null;
            const stateParam = urlParams.get('state');
            if (stateParam) {
                try {
                    const decoded = JSON.parse(atob(stateParam));
                    tenantId = decoded.tenant_id || null;
                } catch {}
            }

            // Send code + tenantId to parent window
            if (window.opener) {
                window.opener.postMessage(
                    { type: 'KEYCLOAK_CALLBACK', code, tenant_id: tenantId },
                    window.location.origin
                );
                setStatus('success');
                setTimeout(() => { window.close(); }, 1500);
            } else {
                const query = new URLSearchParams({ code });
                if (tenantId) query.set('tenant_id', tenantId);
                window.location.href = `/login?${query.toString()}`;
            }
        } else {
            setStatus('error');
            setErrorMessage('Authorization code bulunamadı');
            setTimeout(() => {
                window.close();
            }, 3000);
        }
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-background flex items-center justify-center p-4">
            <div className="max-w-sm w-full">
                <div className="bg-muted/50 border border-border rounded-2xl p-8 text-center backdrop-blur-xl">
                    {/* Logo */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-lg font-bold text-foreground">
                            Uyum<span className="text-primary">Akademi</span>
                        </span>
                    </div>

                    {status === 'processing' && (
                        <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                            </div>
                            <h2 className="text-lg font-semibold text-foreground mb-2">Giriş Yapılıyor</h2>
                            <p className="text-sm text-muted-foreground">
                                Lütfen bekleyin, oturumunuz açılıyor...
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                <CheckCircle2 className="w-8 h-8 text-green-400" />
                            </div>
                            <h2 className="text-lg font-semibold text-foreground mb-2">Giriş Başarılı!</h2>
                            <p className="text-sm text-muted-foreground">
                                Bu pencere otomatik olarak kapanacak...
                            </p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                                <XCircle className="w-8 h-8 text-red-400" />
                            </div>
                            <h2 className="text-lg font-semibold text-foreground mb-2">Giriş Başarısız</h2>
                            <p className="text-sm text-red-400 mb-4">
                                {errorMessage}
                            </p>
                            <button 
                                onClick={() => window.close()}
                                className="px-4 py-2 bg-muted hover:bg-white/20 text-foreground rounded-lg transition-colors text-sm"
                            >
                                Pencereyi Kapat
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KeycloakCallbackPage;
